import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { AvailableLanguages } from '@constants';
import { ConsumableCountry } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { CommonsFacade } from '@state/commons';
import moment from 'moment-timezone';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-long-resi-step1',
  templateUrl: './long-resi-step1.component.html',
  styleUrls: ['./long-resi-step1.component.scss'],
  standalone: false,
})
export class LongResiStep1Component implements OnInit, AfterViewInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly commonsFacade = inject(CommonsFacade);

  @Input() formGroup: FormGroup;
  @Input() mode: 'foreigner' | 'employee';

  @Output() lastFieldTabPressed: EventEmitter<void> = new EventEmitter<void>();

  public controls: { [key: string]: FormControl } = {};
  public currentLang: AvailableLanguages;

  public selectsValues: { [key: string]: { label: string; value: string }[] };

  public minValidDate = moment().subtract(125, 'years');
  public maxValidDate = moment().subtract(1, 'day');
  public maxValidFutureDate = moment().subtract(1, 'day').add(30, 'years');
  public documentIssuePickerStartDate = moment().subtract(10, 'years').startOf('year');
  public documentExpiryPickerStartDate = moment().add(1, 'year').startOf('year');
  public countries: ConsumableCountry[];

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  public ngOnInit(): void {
    this.currentLang = this.translateService.currentLang as AvailableLanguages;
    this.selectsValues = this.translateService.translations[this.currentLang].SELECTS_VALUES;

    this.commonsFacade
      .getCountries$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(ctrs => {
        this.countries = ctrs;
      });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
      this.selectsValues = this.translateService.translations[this.currentLang].SELECTS_VALUES;
    });

    Object.keys(this.formGroup.controls).forEach(fieldName => {
      this.controls[fieldName] = this.formGroup.controls[fieldName] as FormControl;
    });

    this.controls.citizenship.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(citizenship => {
      const country = this.countries.find(ctry => ctry.name === citizenship);
      this.controls.nationality.setValue(country?.nationality || citizenship);
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.hasAnyFieldAnyValue(this.formGroup.controls)) {
        this.formGroup.markAllAsTouched();
      }
    });
  }

  public ngOnDestroy(): void {
    this.controls.countryOfBirth.markAsDirty();
    this.controls.countryOfBirth.markAsTouched();
    this.controls.countryOfBirth.updateValueAndValidity();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private hasAnyFieldAnyValue(controls: { [key: string]: AbstractControl }): boolean {
    return Object.keys(controls).some(controlName => {
      const val = controls[controlName].value;

      if (Array.isArray(val) && val.length > 0) {
        return true;
      }

      return val !== '' && val !== undefined && val !== null;
    });
  }
}
